// src/utils/whatsappInboxUtil.ts
import { toast } from "react-hot-toast";
import { assignConversation } from "../../../services/whatsapp";
import { assignLeadInList } from "../../../services/leadService";
import logger, { Logger } from "../../../utils/logger";

/**
 * Scrolls the chat container to the bottom
 * @param containerRef React ref for the container element
 */
export const scrollToBottom = (containerRef: { current: any; }) => {
  if (!containerRef || !containerRef.current) {
    logger.log("Cannot scroll - invalid reference");
    return;
  }
  
  const container = containerRef.current;
  
  // Force layout recalculation
  void container.offsetHeight;
  
  // Get dimensions after forcing layout
  const scrollHeight = container.scrollHeight;
  const clientHeight = container.clientHeight;
  
  logger.log("Scroll dimensions:", {
    scrollHeight,
    clientHeight,
    difference: scrollHeight - clientHeight
  });
  
  // Try multiple methods to scroll
  // 1. Classic scrollTop
  container.scrollTop = 999999; // Large number to ensure bottom
  
  // 2. ScrollTo API
  try {
    container.scrollTo({
      top: 999999,
      behavior: 'auto'
    });
  } catch (e) {
    console.error("ScrollTo failed:", e);
  }
  
  // 3. Try with a delay
  setTimeout(() => {
    if (container) {
      container.scrollTop = container.scrollHeight;
      logger.log("After delay scrollTop:", container.scrollTop);
    }
  }, 50);
}

/**
 * Handles assigning a chat to a user (directly via API)
 */
export const assignChatToUser = async (
  conversationId: string,
  userId: string
): Promise<any> => {
  try {
    Logger.log("Assigning chat to user", { conversationId, userId });
    
    const data = {
      assignToIds: [userId],
    };
    
    const response = await assignConversation(conversationId, data);
    toast.success("Chat assigned successfully");
    
    return response.data;
  } catch (error) {
    Logger.log("Error assigning chat", error);
    toast.error("Failed to assign chat");
    throw error;
  }
};

/**
 * Handles assigning a lead to a user (directly via API)
 */
export const assignLeadToUser = async (
  leadId: string,
  userId: string
): Promise<any> => {
  try {
    Logger.log("Assigning lead to user", { leadId, userId });
    
    const data = {
      leadIds: [leadId],
      assignToUser: userId,
    };
    
    const response = await assignLeadInList(data);
    toast.success(response?.data?.message || "Lead assigned successfully");
    
    return response.data;
  } catch (error) {
    Logger.log("Error assigning lead", error);
    toast.error("Failed to assign lead");
    throw error;
  }
};

/**
 * Load status data from localStorage
 */
export const loadStatusData = () => {
  try {
    const { status } = JSON.parse(localStorage.getItem("user_preferences") || "{}") || {};
    
    // Convert array to object for easier lookup
    const statusObj = status?.reduce((prev: any, current: any) => {
      prev[current.value] = current;
      return prev;
    }, {}) || {};
    
    return statusObj;
  } catch (error) {
    Logger.log("Error loading status data", error);
    return {};
  }
};

/**
 * Handle checking if a user should see the add chat button
 * based on permissions and subscription
 */
export const canAddNewChat = () => {
  try {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const userPlan = userData?.subscriptionPlanType?.toLowerCase() || '';
    const userRole = userData?.role?.toLowerCase() || '';
    
    // Only certain plans/roles can start new chats
    return userPlan === 'ultimate' || 
           userRole === 'admin' || 
           userRole.includes('manager');
  } catch (error) {
    return false;
  }
};